// Favicon notification badge.
//
// Draws a small red bubble with a count over the app's existing favicon and
// swaps the <link rel="icon"> href to the resulting data-URL. Restores the
// original favicon when the count drops back to 0.
//
// Why a util rather than a CSS trick: browsers cache the <link rel="icon">
// href across pages, so generating a unique data-URL per count is the
// reliable way to force a redraw in every tab.

const ICON_SIZE = 64;          // canvas size — favicons are usually 32 / 64
const BADGE_COLOR = "#E11D48"; // matches --sms-danger
const TEXT_COLOR = "#FFFFFF";

let originalHref = null;       // remembered first favicon URL — used to reset
let cachedBaseImg = null;       // promise of an Image, loaded once

const SVG_FALLBACK =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'>
       <rect width='64' height='64' fill='%230055FF'/>
       <text x='32' y='44' text-anchor='middle' fill='white' font-family='sans-serif' font-weight='bold' font-size='34'>S</text>
     </svg>`
  );

function getLinkEl() {
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  if (originalHref === null) originalHref = link.href || SVG_FALLBACK;
  return link;
}

function loadBaseImage() {
  if (cachedBaseImg) return cachedBaseImg;
  const link = getLinkEl();
  cachedBaseImg = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => {
      // Fall back to the embedded SVG if the link href can't be loaded.
      const fb = new Image();
      fb.onload = () => resolve(fb);
      fb.src = SVG_FALLBACK;
    };
    img.src = originalHref || SVG_FALLBACK;
  });
  return cachedBaseImg;
}

function drawBadge(ctx, count) {
  const text = count > 99 ? "99+" : String(count);
  // Pill on the bottom-right corner. Width grows with digits.
  const padX = text.length > 1 ? 8 : 6;
  ctx.font = "bold 30px sans-serif";
  const textW = Math.max(ctx.measureText(text).width, 18);
  const pillW = Math.min(ICON_SIZE - 4, textW + padX * 2);
  const pillH = 28;
  const pillX = ICON_SIZE - pillW - 2;
  const pillY = ICON_SIZE - pillH - 2;
  const r = pillH / 2;

  ctx.fillStyle = BADGE_COLOR;
  ctx.beginPath();
  ctx.moveTo(pillX + r, pillY);
  ctx.arcTo(pillX + pillW, pillY, pillX + pillW, pillY + pillH, r);
  ctx.arcTo(pillX + pillW, pillY + pillH, pillX, pillY + pillH, r);
  ctx.arcTo(pillX, pillY + pillH, pillX, pillY, r);
  ctx.arcTo(pillX, pillY, pillX + pillW, pillY, r);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = TEXT_COLOR;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, pillX + pillW / 2, pillY + pillH / 2 + 1);
}

export async function setFaviconBadge(count) {
  const link = getLinkEl();
  if (!count || count <= 0) {
    if (originalHref) link.href = originalHref;
    return;
  }
  try {
    const base = await loadBaseImage();
    const canvas = document.createElement("canvas");
    canvas.width = ICON_SIZE;
    canvas.height = ICON_SIZE;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(base, 0, 0, ICON_SIZE, ICON_SIZE);
    drawBadge(ctx, count);
    link.href = canvas.toDataURL("image/png");
  } catch {
    // Silently no-op — favicon badging is purely cosmetic.
  }
}

export function clearFaviconBadge() {
  const link = getLinkEl();
  if (originalHref) link.href = originalHref;
}
